import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import {
  Landmark, HeartPulse, LogOut, Building2, Users, BedDouble, Cross, Baby,
  Baby as PregnantIcon, User, UserCog, MessageSquare, RefreshCw, Info, Printer,
  FileText, MapPin,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  LineChart, Line, Legend, PieChart, Pie,
} from "recharts";
import { toast } from "sonner";
import { ProvinceList, MinistryAlertsPanel } from "@/pages/MinistryExplore";

const CAT_LABEL = { pregnant: "Femmes enceintes", child: "Enfants 0-3 ans", elderly: "Personnes âgées", general: "Général" };
const LANG_LABEL = { fr: "Français", ln: "Lingala", sw: "Swahili", ts: "Tshiluba", kg: "Kikongo" };
const COLOR = { green: "#1E4631", orange: "#D96C4A", red: "#C62828", olive: "#7A8471", sand: "#E5D3B3", blue: "#2E5C7E" };
const PIE_COLORS = [COLOR.green, COLOR.orange, COLOR.olive, COLOR.sand, COLOR.blue, COLOR.red];

export default function MinistryDashboard() {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ministry/stats`);
      setData(r.data);
    } catch (err) {
      toast.error("Impossible de charger les statistiques");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] grid place-items-center">
        <div className="text-[#5C6B61]">Chargement des statistiques nationales…</div>
      </div>
    );
  }

  const o = data.overview;
  const catData = Object.entries(data.by_category || {}).map(([k, v]) => ({ name: CAT_LABEL[k] || k, value: v }));
  const deathsCatData = Object.entries(data.deaths_by_category || {}).map(([k, v]) => ({ name: CAT_LABEL[k] || k, value: v }));
  const hospCatData = Object.entries(data.hospitalized_by_category || {}).map(([k, v]) => ({ name: CAT_LABEL[k] || k, value: v }));
  const langData = Object.entries(data.by_language || {}).map(([k, v]) => ({ name: LANG_LABEL[k] || k, value: v }));

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="ministry-dashboard">
      {/* Header */}
      <header className="bg-[#1E4631] text-white sticky top-0 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#D96C4A] grid place-items-center">
              <Landmark className="w-6 h-6"/>
            </div>
            <div>
              <div className="font-[Outfit] font-bold text-lg leading-tight">
                {data.scope?.type === "provincial" ? `DPS ${data.scope.province}` : "Ministère de la Santé · RDC"}
              </div>
              <div className="text-xs text-white/70 flex items-center gap-1.5">
                {data.scope?.type === "provincial" ? (
                  <><MapPin className="w-3 h-3"/> Vue provinciale — {data.scope.province}</>
                ) : (
                  <>Statistiques nationales KWIKILA Care</>
                )}
                <span className="mx-1">·</span>
                <span>Accès lecture seule</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs">
              <div className="font-semibold">{user?.name}</div>
              <div className="text-white/60">{user?.email}</div>
            </div>
            <Link to="/ministry/report" target="_blank" rel="noopener">
              <Button size="sm" className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white" data-testid="official-report-btn">
                <FileText className="w-4 h-4 mr-1.5"/> Rapport officiel
              </Button>
            </Link>
            <Button size="sm" variant="outline" className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={load} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1.5"/> Actualiser
            </Button>
            <Button size="sm" variant="outline" className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={() => window.print()} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-1.5"/> Imprimer
            </Button>
            <Button size="sm" variant="outline" className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5"/> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Info banner */}
        <div className="p-4 bg-white rounded-xl border-l-4 border-[#1E4631] flex gap-3 text-sm text-[#5C6B61]">
          <Info className="w-5 h-5 text-[#1E4631] shrink-0 mt-0.5"/>
          <div>
            <strong className="text-[#1A1D1A]">
              {data.scope?.type === "provincial"
                ? `Vue provinciale — DPS ${data.scope.province}.`
                : "Vue nationale agrégée."}
            </strong>{" "}
            {data.scope?.type === "provincial"
              ? `Ces statistiques couvrent uniquement les hôpitaux de la province du ${data.scope.province} inscrits sur KWIKILA Care.`
              : "Ces statistiques couvrent l'ensemble du territoire de la République Démocratique du Congo à partir des hôpitaux partenaires actifs de KWIKILA Care."}
            {" "}Aucune donnée nominative n'est exposée conformément au secret médical.
            <div className="text-xs mt-1 text-[#8A9990]">Généré le {new Date(data.generated_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</div>
          </div>
        </div>

        {/* Alertes épidémiques */}
        <MinistryAlertsPanel />

        {/* Overview KPI */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-[#5C6B61] mb-3">Vue d'ensemble nationale</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi icon={Building2} color={COLOR.green} label="Hôpitaux actifs" value={o.hospitals_active} sub={`sur ${o.hospitals_total} inscrits`}/>
            <Kpi icon={Users} color={COLOR.olive} label="Patients suivis" value={o.patients_total.toLocaleString("fr-FR")} sub={`dont ${o.patients_active} actifs`}/>
            <Kpi icon={BedDouble} color={COLOR.orange} label="Hospitalisations en cours" value={o.patients_hospitalized}/>
            <Kpi icon={Cross} color={COLOR.red} label="Décès enregistrés" value={o.patients_deceased} sub="tous hôpitaux confondus"/>
            <Kpi icon={UserCog} color={COLOR.green} label="Rendez-vous programmés" value={o.appointments_total.toLocaleString("fr-FR")}/>
            <Kpi icon={MessageSquare} color={COLOR.olive} label="SMS de rappel envoyés" value={o.sms_total.toLocaleString("fr-FR")}/>
            <Kpi icon={PregnantIcon} color={COLOR.orange} label="Femmes enceintes suivies" value={data.by_category?.pregnant || 0}/>
            <Kpi icon={Baby} color={COLOR.green} label="Enfants 0-3 ans suivis" value={data.by_category?.child || 0}/>
          </div>
        </section>

        {/* Explorer par province */}
        <section data-testid="explore-section">
          <h2 className="text-xs uppercase tracking-widest text-[#5C6B61] mb-1">Explorer par province</h2>
          <p className="text-xs text-[#8A9990] mb-3">Cliquez sur une province pour voir ses hôpitaux, puis sur un hôpital pour le détail de ses enregistrements (agrégés, sans données nominatives).</p>
          <ProvinceList />
        </section>

        {/* Charts row 1 */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card title="Évolution 6 derniers mois">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.trend_6m || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                <XAxis dataKey="label" stroke="#5C6B61" fontSize={11}/>
                <YAxis stroke="#5C6B61" fontSize={11}/>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7" }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Line type="monotone" dataKey="new_patients" name="Nouveaux patients" stroke={COLOR.green} strokeWidth={2} dot/>
                <Line type="monotone" dataKey="hospitalizations" name="Hospitalisations" stroke={COLOR.orange} strokeWidth={2} dot/>
                <Line type="monotone" dataKey="deaths" name="Décès" stroke={COLOR.red} strokeWidth={2} dot/>
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Répartition patients par catégorie">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                  {catData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </section>

        {/* Charts row 2 */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card title="Décès par tranche d'âge">
            {deathsCatData.every(x => x.value === 0) ? (
              <EmptyChart>Aucun décès enregistré pour l'instant.</EmptyChart>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={deathsCatData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                  <XAxis dataKey="name" stroke="#5C6B61" fontSize={11}/>
                  <YAxis stroke="#5C6B61" fontSize={11} allowDecimals={false}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7" }}/>
                  <Bar dataKey="value" name="Décès" fill={COLOR.red} radius={[6, 6, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Hospitalisations par tranche d'âge">
            {hospCatData.every(x => x.value === 0) ? (
              <EmptyChart>Aucune hospitalisation en cours.</EmptyChart>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hospCatData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                  <XAxis dataKey="name" stroke="#5C6B61" fontSize={11}/>
                  <YAxis stroke="#5C6B61" fontSize={11} allowDecimals={false}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7" }}/>
                  <Bar dataKey="value" name="Hospitalisations" fill={COLOR.orange} radius={[6, 6, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </section>

        {/* Charts row 3 */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card title="Principales causes de décès">
            {(!data.death_causes || data.death_causes.length === 0) ? (
              <EmptyChart>Aucune cause enregistrée.</EmptyChart>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.death_causes} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                  <XAxis type="number" stroke="#5C6B61" fontSize={11} allowDecimals={false}/>
                  <YAxis type="category" dataKey="cause" stroke="#5C6B61" fontSize={11} width={120}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7" }}/>
                  <Bar dataKey="count" name="Cas" fill={COLOR.red} radius={[0, 6, 6, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Adoption linguistique des patients">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={langData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                <XAxis dataKey="name" stroke="#5C6B61" fontSize={11}/>
                <YAxis stroke="#5C6B61" fontSize={11} allowDecimals={false}/>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7" }}/>
                <Bar dataKey="value" name="Patients" fill={COLOR.green} radius={[6, 6, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </section>

        {/* Geographic */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card title="Hôpitaux par ville">
            {(!data.by_city || data.by_city.length === 0) ? (
              <EmptyChart>Aucune donnée géographique.</EmptyChart>
            ) : (
              <div className="divide-y divide-[#E5EBE7]" data-testid="cities-list">
                {data.by_city.map((c, idx) => (
                  <div key={idx} className="py-2 flex items-center justify-between">
                    <span className="text-sm">{c.city}</span>
                    <span className="text-xs font-semibold text-[#1E4631]">{c.hospitals} hôpital{c.hospitals > 1 ? "s" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Communes avec le plus de patients">
            {(!data.by_commune || data.by_commune.length === 0) ? (
              <EmptyChart>Adresses communales non encore renseignées pour les patients.</EmptyChart>
            ) : (
              <div className="divide-y divide-[#E5EBE7]" data-testid="communes-list">
                {data.by_commune.map((c, idx) => (
                  <div key={idx} className="py-2 flex items-center justify-between">
                    <span className="text-sm">{c.commune}</span>
                    <span className="text-xs font-semibold text-[#D96C4A]">{c.patients} patient{c.patients > 1 ? "s" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </section>

        {/* Footer signature */}
        <footer className="text-center text-xs text-[#5C6B61] pt-8 pb-6 border-t border-[#E5EBE7]">
          <div className="inline-flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-[#1E4631]"/>
            <span>KWIKILA Care · La santé en confiance · Kinshasa, RDC</span>
          </div>
          <div className="mt-1">Rapport généré automatiquement · Données mises à jour en temps réel</div>
        </footer>
      </main>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5EBE7] p-4">
      <div className="flex items-center gap-2 text-xs text-[#5C6B61] uppercase tracking-wider">
        <Icon className="w-4 h-4" style={{ color }}/>
        {label}
      </div>
      <div className="mt-2 text-2xl font-[Outfit] font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-[#5C6B61] mt-1">{sub}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5EBE7] p-5">
      <h3 className="font-[Outfit] font-semibold text-sm text-[#1E4631] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ children }) {
  return (
    <div className="h-[240px] grid place-items-center text-sm text-[#5C6B61] italic">
      {children}
    </div>
  );
}
